/**
 * collectionStore — 앨범 컬렉션(선반) 로컬 상태.
 *
 * 설계:
 *   - Zustand + persist 로 localStorage 영속화.
 *   - 컬렉션 CRUD + 앨범-컬렉션 소속 관리.
 *   - 실제 앨범 메타(title, artist 등)는 서버 API 응답을 그대로 쓰고,
 *     "어떤 앨범이 어떤 컬렉션에 속하는가"만 로컬에 저장.
 *   - 기본 "전체" 컬렉션은 id='col_default', 삭제 불가.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Collection, CollectionMember } from '../types/collection';
import * as socialApi from '../api/social.api';

/** 현재 시각 ISO 문자열. */
function nowIso(): string {
  return new Date().toISOString();
}

/** UUID 생성 — crypto.randomUUID 우선, 폴백은 random. */
function genId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/** 기본 컬렉션 — 항상 존재, 삭제 불가. */
const DEFAULT_COLLECTION_ID = 'col_default';

const DEFAULT_COLLECTION: Collection = {
  id: DEFAULT_COLLECTION_ID,
  name: '전체',
  createdAt: nowIso(),
};

// ──────────────────────────────────────────────
// ui slice — Fold 리사이즈 입력 유실 방지용 ephemeral UI state
// ──────────────────────────────────────────────

export type ActiveSheet = 'none' | 'save-to' | 'add-albums' | 'cleanup' | 'create';

export interface CollectionUiState {
  /** 현재 열려있는 시트 종류. */
  activeSheet: ActiveSheet;
  /** 공용 검색 쿼리 (AddToCollectionSheet, AddAlbumsToCollectionDialog). */
  query: string;
  /** 앨범 다중 선택 (CleanupInactiveSheet, AddAlbumsToCollectionDialog). */
  selectedAlbumIds: string[];
  /** 컬렉션 다중 토글 (AddToCollectionSheet). */
  selectedCollectionIds: string[];
  /** 컬렉션 생성/편집 임시 입력. */
  draft: { name: string; description: string; isPublic: boolean };
  /** AddToCollectionSheet 내부 inline 생성 폼 토글. */
  showCreate: boolean;
  /** 데스크탑 popover 위치 기준 DOMRect 스냅샷. */
  triggerRect: { x: number; y: number; width: number; height: number } | null;
}

export const INITIAL_UI_STATE: CollectionUiState = {
  activeSheet: 'none',
  query: '',
  selectedAlbumIds: [],
  selectedCollectionIds: [],
  draft: { name: '', description: '', isPublic: false },
  showCreate: false,
  triggerRect: null,
};

export interface CollectionStoreState {
  /** 컬렉션 목록 (배열 순서 = 선반 순서). */
  collections: Collection[];
  /** 앨범-컬렉션 소속 목록. */
  members: CollectionMember[];

  // ui slice ─────────────────────────────────────
  /** Fold 리사이즈 입력 유실 방지용 ephemeral UI state. */
  ui: CollectionUiState;
  /** ui slice 부분 업데이트 — immer 없이 shallow merge. */
  setUi: (partial: Partial<CollectionUiState>) => void;
  /** 검색 쿼리만 업데이트. */
  setUiQuery: (q: string) => void;
  /** 앨범 id 토글 (selectedAlbumIds). */
  toggleAlbumId: (id: string) => void;
  /** 컬렉션 id 토글 (selectedCollectionIds). */
  toggleCollectionId: (id: string) => void;
  /** draft 단일 필드 업데이트. */
  setDraftField: (k: keyof CollectionUiState['draft'], v: string | boolean) => void;
  /** ui slice 전체 초기화 (시트 dismiss 시 호출). */
  resetUi: () => void;
  /** 시트 열기 + activeSheet 설정. */
  openSheet: (kind: ActiveSheet, triggerRect?: DOMRect | null) => void;
  /** 시트 닫기 + ui 전체 초기화. */
  closeSheet: () => void;

  // 컬렉션 CRUD ──────────────────────────────────
  addCollection: (name: string) => Collection;
  renameCollection: (id: string, name: string) => void;
  /** 기본 컬렉션('col_default')은 삭제 불가. 삭제 시 소속 앨범은 기본 컬렉션으로 이관. */
  removeCollection: (id: string) => void;
  /** 컬렉션 전체 순서 재정렬 — 정렬된 id 배열로 교체. */
  reorderCollections: (ids: string[]) => void;

  // 앨범 소속 관리 ────────────────────────────────
  /** albumId 를 특정 컬렉션으로 이동. 기존 소속이 없으면 신규 추가. */
  moveAlbumToCollection: (albumId: string, collectionId: string) => void;
  /** albumId 의 소속 컬렉션 id 를 반환. 없으면 기본 컬렉션 id. */
  getCollectionIdForAlbum: (albumId: string) => string;
  /** 컬렉션에 소속된 albumId 목록을 반환. */
  getAlbumIdsForCollection: (collectionId: string) => string[];
  /** 앨범 삭제 시 소속 정보도 제거. */
  removeAlbumMembership: (albumId: string) => void;

  /** 스토어 초기화 (디버그용). */
  reset: () => void;

  // 서버 컬렉션 동기화 ────────────────────────────
  /** 서버에서 가져온 컬렉션 목록. */
  serverCollections: socialApi.Collection[];
  serverLoading: boolean;

  /** 최근 사용한 컬렉션 id 목록 — 최대 5개, 첫 번째가 가장 최근. (Save Sheet 단축 진입용) */
  recentCollectionIds: string[];

  /** 앨범별 membership 캐시 — 단일 앨범의 소속 컬렉션 id 배열을 메모리 캐시. */
  membershipCache: Record<string, string[]>;

  /** 내 컬렉션 목록을 서버에서 가져와 serverCollections 업데이트. */
  fetchServerCollections: () => Promise<void>;
  /** 서버에 새 컬렉션 생성 후 serverCollections에 추가. 생성된 컬렉션을 반환. */
  createServerCollection: (name: string, description?: string, isPublic?: boolean) => Promise<socialApi.Collection>;
  /** 서버 컬렉션 삭제 후 serverCollections에서 제거. */
  deleteServerCollection: (id: string) => Promise<void>;
  /** 서버 컬렉션에 앨범 추가. */
  addAlbumToServerCollection: (collectionId: string, albumId: string) => Promise<void>;
  /** 서버 컬렉션에서 앨범 제거. */
  removeAlbumFromServerCollection: (collectionId: string, albumId: string) => Promise<void>;

  /** 최근 사용 컬렉션 push — 맨 앞에 추가, 중복 제거, 최대 5개. */
  pushRecentCollection: (id: string) => void;

  /**
   * 다중 앨범을 단일 컬렉션에 일괄 추가.
   * - BE bulk endpoint 사용. added/failed 분리 결과 반환.
   * - serverCollections, recentCollectionIds 자동 업데이트.
   */
  bulkAddAlbums: (
    collectionId: string,
    albumIds: string[],
  ) => Promise<socialApi.BulkAddAlbumsResult>;

  /**
   * 특정 앨범의 컬렉션 멤버십 조회.
   * - 결과를 membershipCache에 저장.
   * - force=true면 캐시 무시.
   */
  fetchMembership: (albumId: string, force?: boolean) => Promise<string[]>;
}

export const useCollectionStore = create<CollectionStoreState>()(
  persist(
    (set, get) => ({
      collections: [DEFAULT_COLLECTION],
      members: [],

      // ui slice ──────────────────────────────────
      ui: { ...INITIAL_UI_STATE },

      setUi: (partial) => {
        set((s) => ({ ui: { ...s.ui, ...partial } }));
      },

      setUiQuery: (q) => {
        set((s) => ({ ui: { ...s.ui, query: q } }));
      },

      toggleAlbumId: (id) => {
        set((s) => {
          const cur = s.ui.selectedAlbumIds;
          const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
          return { ui: { ...s.ui, selectedAlbumIds: next } };
        });
      },

      toggleCollectionId: (id) => {
        set((s) => {
          const cur = s.ui.selectedCollectionIds;
          const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
          return { ui: { ...s.ui, selectedCollectionIds: next } };
        });
      },

      setDraftField: (k, v) => {
        set((s) => ({
          ui: {
            ...s.ui,
            draft: { ...s.ui.draft, [k]: v },
          },
        }));
      },

      resetUi: () => {
        set({ ui: { ...INITIAL_UI_STATE } });
      },

      openSheet: (kind, triggerRect) => {
        const rect = triggerRect
          ? { x: triggerRect.x, y: triggerRect.y, width: triggerRect.width, height: triggerRect.height }
          : null;
        set((s) => ({
          ui: { ...INITIAL_UI_STATE, activeSheet: kind, triggerRect: rect ?? s.ui.triggerRect },
        }));
      },

      closeSheet: () => {
        set({ ui: { ...INITIAL_UI_STATE } });
      },

      addCollection: (name) => {
        const col: Collection = {
          id: genId('col'),
          name: name.trim() || '새 컬렉션',
          createdAt: nowIso(),
        };
        set((s) => ({ collections: [...s.collections, col] }));
        return col;
      },

      renameCollection: (id, name) => {
        // 기본 컬렉션 이름 변경 허용
        const trimmed = name.trim();
        if (!trimmed) return;
        set((s) => ({
          collections: s.collections.map((c) =>
            c.id === id ? { ...c, name: trimmed } : c,
          ),
        }));
      },

      removeCollection: (id) => {
        // 기본 컬렉션 삭제 불가
        if (id === DEFAULT_COLLECTION_ID) return;
        const { collections } = get();
        if (collections.length <= 1) return;
        set((s) => ({
          collections: s.collections.filter((c) => c.id !== id),
          // 삭제된 컬렉션의 앨범 → 기본 컬렉션으로 이관
          members: s.members.map((m) =>
            m.collectionId === id
              ? { ...m, collectionId: DEFAULT_COLLECTION_ID }
              : m,
          ),
        }));
      },

      reorderCollections: (ids) => {
        set((s) => {
          const map = new Map(s.collections.map((c) => [c.id, c]));
          const next: Collection[] = [];
          for (const id of ids) {
            const c = map.get(id);
            if (c) {
              next.push(c);
              map.delete(id);
            }
          }
          // ids 에 누락된 항목은 뒤에 유지
          for (const c of map.values()) next.push(c);
          return { collections: next };
        });
      },

      moveAlbumToCollection: (albumId, collectionId) => {
        set((s) => {
          const exists = s.members.some((m) => m.albumId === albumId);
          if (exists) {
            return {
              members: s.members.map((m) =>
                m.albumId === albumId ? { ...m, collectionId } : m,
              ),
            };
          }
          return {
            members: [...s.members, { albumId, collectionId }],
          };
        });
      },

      getCollectionIdForAlbum: (albumId) => {
        const member = get().members.find((m) => m.albumId === albumId);
        return member?.collectionId ?? DEFAULT_COLLECTION_ID;
      },

      getAlbumIdsForCollection: (collectionId) => {
        return get()
          .members.filter((m) => m.collectionId === collectionId)
          .map((m) => m.albumId);
      },

      removeAlbumMembership: (albumId) => {
        set((s) => ({
          members: s.members.filter((m) => m.albumId !== albumId),
        }));
      },

      reset: () => {
        set({ collections: [DEFAULT_COLLECTION], members: [] });
      },

      // 서버 컬렉션 동기화 ────────────────────────────
      serverCollections: [],
      serverLoading: false,
      recentCollectionIds: [],
      membershipCache: {},

      fetchServerCollections: async () => {
        set({ serverLoading: true });
        try {
          const data = await socialApi.getMyCollections();
          set({ serverCollections: data });
        } finally {
          set({ serverLoading: false });
        }
      },

      createServerCollection: async (name, description, isPublic) => {
        const payload: socialApi.CollectionPayload = { name, description, isPublic };
        const created = await socialApi.createCollection(payload);
        set((s) => ({ serverCollections: [...s.serverCollections, created] }));
        return created;
      },

      deleteServerCollection: async (id) => {
        const prev = get().serverCollections;
        set((s) => ({ serverCollections: s.serverCollections.filter((c) => c._id !== id) }));
        try {
          await socialApi.deleteCollection(id);
        } catch (err) {
          set({ serverCollections: prev });
          throw err;
        }
      },

      addAlbumToServerCollection: async (collectionId, albumId) => {
        const updated = await socialApi.addAlbumToCollection(collectionId, albumId);
        set((s) => ({
          serverCollections: s.serverCollections.map((c) =>
            c._id === collectionId ? updated : c,
          ),
        }));
      },

      removeAlbumFromServerCollection: async (collectionId, albumId) => {
        const prev = get().serverCollections;
        set((s) => ({
          serverCollections: s.serverCollections.map((c) =>
            c._id === collectionId
              ? { ...c, albums: c.albums.filter((a) => a !== albumId) }
              : c,
          ),
        }));
        try {
          const updated = await socialApi.removeAlbumFromCollection(collectionId, albumId);
          set((s) => ({
            serverCollections: s.serverCollections.map((c) =>
              c._id === collectionId ? updated : c,
            ),
          }));
        } catch (err) {
          set({ serverCollections: prev });
          throw err;
        }
      },

      pushRecentCollection: (id) => {
        set((s) => {
          // 중복 제거하고 맨 앞에 삽입, 최대 5개
          const filtered = s.recentCollectionIds.filter((x) => x !== id);
          return { recentCollectionIds: [id, ...filtered].slice(0, 5) };
        });
      },

      bulkAddAlbums: async (collectionId, albumIds) => {
        // optimistic UI를 위해 즉시 push
        const result = await socialApi.bulkAddAlbumsToCollection(collectionId, albumIds);

        // 성공한 albumId 만 store 반영
        if (result.added.length > 0) {
          set((s) => {
            const next = s.serverCollections.map((c) => {
              if (c._id !== collectionId) return c;
              const merged = Array.from(new Set([...c.albums, ...result.added]));
              return { ...c, albums: merged };
            });
            // membershipCache 동기화
            const cache = { ...s.membershipCache };
            for (const id of result.added) {
              const cur = cache[id] ?? [];
              if (!cur.includes(collectionId)) {
                cache[id] = [...cur, collectionId];
              }
            }
            return { serverCollections: next, membershipCache: cache };
          });
          // recent 갱신
          get().pushRecentCollection(collectionId);
        }

        return result;
      },

      fetchMembership: async (albumId, force) => {
        if (!force) {
          const cached = get().membershipCache[albumId];
          if (cached) return cached;
        }
        const res = await socialApi.getCollectionsMembership(albumId);
        set((s) => ({
          membershipCache: { ...s.membershipCache, [albumId]: res.collectionIds },
        }));
        return res.collectionIds;
      },
    }),
    {
      name: 'md-label-maker.collections.v1',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        collections: s.collections,
        members: s.members,
        recentCollectionIds: s.recentCollectionIds,
      }),
    },
  ),
);

/** 기본 컬렉션 id 상수 — 외부에서 참조 가능. */
export { DEFAULT_COLLECTION_ID };
